import { createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { apiSlice } from '../../app/api/apiSlice';

const ordersAdapter = createEntityAdapter({
  selectId: (order) => order.id,
});

const initialState = ordersAdapter.getInitialState({ total: 0 });

export const ordersApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getOrders: builder.query({
      query: (params = {}) => ({
        url: '/orders',
        params,
      }),

      transformResponse: (response, meta) => {
        let items = [];
        let total = 0;

        if (response && typeof response === 'object' && 'items' in response && 'total' in response) {
          items = response.items ?? [];
          total = Number(response.total ?? 0);
        } else if (Array.isArray(response)) {
          items = response;
          const headerTotal =
            meta?.response?.headers?.get('X-Total-Count') ??
            meta?.response?.headers?.get('x-total-count');
          total = headerTotal ? Number(headerTotal) : items.length;
        }

        const processed = items.map((order) => {
          const sid = String(order?.order_id ?? order?.id ?? order?._id ?? '');
          return {
            ...order,
            id: sid,
            _id: sid,
          };
        });

        return ordersAdapter.setAll(
          { ...initialState, total },
          processed
        );
      },

      providesTags: (result) =>
        result?.ids
          ? [
              ...result.ids.map((id) => ({ type: 'Order', id })),
              { type: 'Order', id: 'LIST' },
            ]
          : [{ type: 'Order', id: 'LIST' }],
    }),

    getOrderById: builder.query({
      query: (id) => `/orders/${id}`,
      providesTags: (_res, _err, id) => [{ type: 'Order', id }],
    }),

    addNewOrder: builder.mutation({
      query: (body) => ({
        url: '/orders',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Order', id: 'LIST' }],
    }),

    updateOrder: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/orders/${id}`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Order', id },
        { type: 'Order', id: 'LIST' },
      ],
    }),

    deleteOrder: builder.mutation({
      query: ({ id }) => ({
        url: `/orders/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Order', id },
        { type: 'Order', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetOrdersQuery,
  useGetOrderByIdQuery,
  useAddNewOrderMutation,
  useUpdateOrderMutation,
  useDeleteOrderMutation,
} = ordersApiSlice;

const selectGetOrdersResult = ordersApiSlice.endpoints.getOrders.select;

export const makeSelectOrdersData = (args) =>
  createSelector(selectGetOrdersResult(args), (res) => res?.data ?? initialState);

export const makeOrderSelectors = (args) => {
  const selectData = makeSelectOrdersData(args);
  const base = ordersAdapter.getSelectors((state) => selectData(state));

  return {
    selectAllOrders: base.selectAll,
    selectOrderById: base.selectById,
    selectOrderIds: base.selectIds,
    selectOrderEntities: base.selectEntities,
    selectTotal: (state) => selectData(state).total ?? 0,
  };
};